import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
  IconButton,
  Tooltip,
  makeStyles
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import BuildIcon from '@material-ui/icons/Build';
import KeyIcon from '@material-ui/icons/VpnKey';
import InfoIcon from '@material-ui/icons/Info';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteIcon from '@material-ui/icons/Delete';
import PIVKeySetupWizard from './PIVKeySetupWizard';
import PIVKeyImportDialog from './PIVKeyImportDialog';
import CertificateGenerationDialog from './CertificateGenerationDialog';
import { transceive } from './actions';
import { stringToHex, hexStringToString } from './backupService';
import { detectPIVCapabilities } from './capabilityDetection';

// PIV slot definitions
const PIV_SLOTS = [
  { id: '9A', tag: '5FC105', name: 'PIV Authentication', description: 'Login, VPN, SSH authentication' },
  { id: '9C', tag: '5FC10A', name: 'Digital Signature', description: 'Document and email signing' },
  { id: '9D', tag: '5FC10B', name: 'Key Management', description: 'Encryption and decryption' },
  { id: '9E', tag: '5FC101', name: 'Card Authentication', description: 'Physical access control' }
];

// Styles for Material-UI v4
const useStyles = makeStyles((theme) => ({
  wizardCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
    color: 'white',
    padding: theme.spacing(3)
  },
  wizardIcon: {
    fontSize: 48,
    color: 'white',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
  },
  wizardButton: {
    backgroundColor: 'white',
    color: '#667eea',
    fontWeight: 600,
    fontSize: '1.1rem',
    padding: theme.spacing(1.5),
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    '&:hover': {
      backgroundColor: '#f0f0f0',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
    },
    transition: 'all 0.3s ease'
  },
  wizardAlert: {
    marginBottom: theme.spacing(2),
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: 'white',
    backdropFilter: 'blur(10px)',
    '& .MuiAlert-icon': {
      color: 'white'
    }
  },
  wizardDivider: {
    margin: theme.spacing(2, 0),
    borderColor: 'rgba(255,255,255,0.3)'
  },
  sectionDivider: {
    margin: theme.spacing(4, 0)
  },
  dividerChip: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    fontWeight: 500
  }
}));

const Piv = ({ dispatch }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [slotStatuses, setSlotStatuses] = useState({});
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedSlotName, setSelectedSlotName] = useState('');
  const [importedKeyData, setImportedKeyData] = useState(null);

  useEffect(() => {
    loadSlotStatuses();
    loadDeviceInfo();
    loadCapabilities();
  }, []);

  const loadCapabilities = async () => {
    try {
      const caps = await detectPIVCapabilities(dispatch);
      setCapabilities(caps);
    } catch (error) {
      console.error('Failed to load capabilities:', error);
    }
  };

  const loadDeviceInfo = async () => {
    try {
      await dispatch(transceive('00A4040009A00000030800001000'));

      // Get version
      const versionRes = await dispatch(transceive('00FD000000'));
      if (versionRes.endsWith('9000')) {
        const version = versionRes.substring(0, versionRes.length - 4);
        setDeviceInfo({ version });
      }
    } catch (error) {
      console.error('Failed to load device info:', error);
    }
  };

  const loadSlotStatuses = async () => {
    setLoading(true);
    const statuses = {};

    try {
      await dispatch(transceive('00A4040009A00000030800001000'));

      for (const slot of PIV_SLOTS) {
        try {
          // Try to read certificate
          const tagHex = slot.tag;
          const lc = (tagHex.length / 2).toString(16).padStart(2, '0');
          const apdu = `00CB3FFF${lc}5C03${tagHex}`;
          const response = await dispatch(transceive(apdu));

          if (response.endsWith('9000') && response.length > 4) {
            const certData = response.slice(0, -4);
            statuses[slot.id] = {
              hasKey: true,
              hasCertificate: certData.length > 0,
              certificateSize: certData.length / 2
            };
          } else if (response.slice(-4).startsWith('61')) {
            // Has data but needs chunked reading
            statuses[slot.id] = {
              hasKey: true,
              hasCertificate: true,
              certificateSize: parseInt(response.slice(-2), 16)
            };
          } else {
            statuses[slot.id] = {
              hasKey: false,
              hasCertificate: false
            };
          }
        } catch (error) {
          statuses[slot.id] = {
            hasKey: false,
            hasCertificate: false,
            error: error.message
          };
        }
      }

      setSlotStatuses(statuses);
    } catch (error) {
      console.error('Failed to load slot statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImportKey = async (slotId) => {
    const slot = PIV_SLOTS.find(s => s.id === slotId);
    setSelectedSlot(slotId);
    setSelectedSlotName(slot?.name || '');
    setImportDialogOpen(true);
  };

  const handleImportSuccess = (slot, keyData) => {
    setImportedKeyData(keyData);
    loadSlotStatuses();

    // Prompt to generate certificate
    setTimeout(() => {
      if (window.confirm('Key imported successfully! Do you want to generate a self-signed certificate for this key?')) {
        setImportDialogOpen(false);
        setCertDialogOpen(true);
      } else {
        setImportedKeyData(null);
      }
    }, 500);
  };

  const handleGenerateCertificate = (slotId) => {
    const slot = PIV_SLOTS.find(s => s.id === slotId);
    setSelectedSlot(slotId);
    setSelectedSlotName(slot?.name || '');
    setImportedKeyData(null); // No imported key data, will extract from card
    setCertDialogOpen(true);
  };

  const handleCertificateGenerated = () => {
    loadSlotStatuses();
    setImportedKeyData(null);
  };

  const handleDeleteKey = async (slotId) => {
    if (!window.confirm(`Are you sure you want to delete the key in slot ${slotId}? This action cannot be undone.`)) {
      return;
    }

    try {
      const pinInput = window.prompt('Enter PIN to confirm deletion:');
      if (!pinInput) return;

      await dispatch(transceive('00A4040009A00000030800001000'));

      // Verify PIN
      const pinHex = stringToHex(pinInput);
      const verifyAPDU = `00200080${(pinHex.length / 2).toString(16).padStart(2, '0')}${pinHex}`;
      const verifyRes = await dispatch(transceive(verifyAPDU));

      if (!verifyRes.endsWith('9000')) {
        alert('PIN verification failed');
        return;
      }

      // Delete by importing an empty/dummy key would require implementation
      alert('Key deletion requires reimplementation. For now, you can reset the entire PIV applet or overwrite with a new key.');

    } catch (error) {
      alert('Deletion failed: ' + error.message);
    }
  };

  const getSlotStatusChip = (status) => {
    if (!status) {
      return <Chip label="Unknown" size="small" />;
    }

    if (status.error) {
      return <Chip label="Error" size="small" color="error" />;
    }

    if (status.hasKey && status.hasCertificate) {
      return <Chip label="Key + Certificate" size="small" color="primary" icon={<KeyIcon />} />;
    }

    if (status.hasKey) {
      return <Chip label="Key Only" size="small" color="warning" icon={<KeyIcon />} />;
    }

    return <Chip label="Empty" size="small" color="default" />;
  };

  return (
    <Container maxWidth="lg" style={{ marginTopadding: 242, marginBottom: 32 }}>
      <Box style={{ marginBottom: 32 }}>
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <Typography variant="h4" component="h1">
            PIV Management
          </Typography>
          <Box>
            <Tooltip title="Refresh status">
              <IconButton onClick={loadSlotStatuses} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Alert severity="info" style={{ marginBottom: 24 }}>
          <AlertTitle>What is PIV?</AlertTitle>
          PIV (Personal Identity Verification) is a US government standard for secure identity credentials.
          It provides cryptographic keys for authentication, signing, and encryption.
        </Alert>

        {deviceInfo && (
          <Card style={{ marginBottom: 24 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Device Information</Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell><strong>PIV Version</strong></TableCell>
                    <TableCell>{deviceInfo.version || 'Unknown'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Applet Status</strong></TableCell>
                    <TableCell><Chip label="Active" size="small" color="primary" /></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </Box>

      <Box style={{ marginBottom: 32 }}>
        <Card variant="outlined" className={classes.wizardCard}>
          <CardContent>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
              <BuildIcon className={classes.wizardIcon} />
              <Box style={{ flex: 1 }}>
                <Typography variant="h5" style={{ fontWeight: 600, color: 'white', marginBottom: 4 }}>
                  🚀 Quick Start: Batch Key Setup Wizard
                </Typography>
                <Typography variant="body1" style={{ color: 'rgba(255,255,255,0.95)' }}>
                  Generate keys for all 4 slots at once - Perfect for first-time setup!
                </Typography>
              </Box>
            </Box>
            <Divider className={classes.wizardDivider} />
            <Alert severity="info" className={classes.wizardAlert}>
              <AlertTitle style={{ color: 'white', fontWeight: 600 }}>✨ Wizard Features</AlertTitle>
              <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.95)' }}>
                • <strong>Batch Configuration:</strong> Set up all 4 key slots (9A, 9C, 9D, 9E) in one go<br/>
                • <strong>Card-Generated Keys:</strong> Generate secure keys directly on your device<br/>
                • <strong>Algorithm Selection:</strong> Choose RSA-2048, ECC P-256, or other algorithms<br/>
                • <strong>Guided Process:</strong> Step-by-step instructions for beginners
              </Typography>
            </Alert>
            <Button
              variant="contained"
              size="large"
              startIcon={<BuildIcon />}
              onClick={() => setWizardOpen(true)}
              fullWidth
              className={classes.wizardButton}
            >
              Launch Batch Setup Wizard
            </Button>
          </CardContent>
        </Card>
      </Box>

      <Box style={{ margin: '32px 0', textAlign: 'center' }}>
        <Divider className={classes.sectionDivider} />
        <Chip label="OR Configure Individual Slots Below" className={classes.dividerChip} />
        <Divider className={classes.sectionDivider} />
      </Box>

      <Typography variant="h5" gutterBottom style={{ marginTopadding: 242, marginBottom: 16 }}>
        Individual Key Slots
      </Typography>

      <Alert severity="info" style={{ marginBottom: 24 }}>
        <AlertTitle>Individual Slot Management</AlertTitle>
        Use the buttons below to configure each slot separately. You can <strong>import external keys</strong> or <strong>generate keys on card</strong> for any individual slot.
      </Alert>

      {loading && <LinearProgress style={{ marginBottom: 16 }} />}

      <Grid container spacing={3}>
        {PIV_SLOTS.map((slot) => {
          const status = slotStatuses[slot.id];
          return (
            <Grid item xs={12} md={6} key={slot.id}>
              <Card>
                <CardContent>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <Typography variant="h6">
                      Slot {slot.id}
                    </Typography>
                    {getSlotStatusChip(status)}
                  </Box>

                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    {slot.name}
                  </Typography>

                  <Typography variant="body2" color="textSecondary" paragraph>
                    {slot.description}
                  </Typography>

                  {status && (
                    <Box style={{ marginTop: 16 }}>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell>
                              {status.hasKey ? '✓ Key present' : '✗ No key'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Certificate</strong></TableCell>
                            <TableCell>
                              {status.hasCertificate ? `✓ Present (${status.certificateSize} bytes)` : '✗ Not found'}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => handleImportKey(slot.id)}
                    disabled={loading}
                  >
                    Import Key
                  </Button>
                  {status?.hasKey && !status?.hasCertificate && (
                    <Button
                      size="small"
                      startIcon={<KeyIcon />}
                      onClick={() => handleGenerateCertificate(slot.id)}
                      disabled={loading}
                      color="primary"
                    >
                      Generate Cert
                    </Button>
                  )}
                  {status?.hasKey && (
                    <Button
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteKey(slot.id)}
                      disabled={loading}
                      color="error"
                    >
                      Delete
                    </Button>
                  )}
                  <Button
                    size="small"
                    startIcon={<InfoIcon />}
                    disabled={!status?.hasKey}
                  >
                    Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box style={{ marginTopadding: 242 }}>
        <Alert severity="warning">
          <AlertTitle>Important: Key Backup Strategy</AlertTitle>
          <Typography variant="body2" paragraph>
            <strong>Card-Generated Keys:</strong> Keys generated inside the CanoKey device cannot be exported or backed up.
            If you lose your device, these keys cannot be recovered.
          </Typography>
          <Typography variant="body2">
            <strong>External Keys:</strong> If you need backup capability, generate keys externally using tools like OpenSSL,
            keep encrypted backups, and import them to your CanoKey device. Use the wizard for guided setup.
          </Typography>
        </Alert>
      </Box>

      <Box style={{ marginTopadding: 164 }}>
        <Alert severity="info">
          <AlertTitle>Need Help?</AlertTitle>
          <Typography variant="body2">
            • <strong>First time setup:</strong> Use the Key Setup Wizard above<br />
            • <strong>Import external keys:</strong> Click "Import Key" on any slot<br />
            • <strong>Advanced operations:</strong> Use command-line tools like yubico-piv-tool or opensc<br />
            • <strong>Documentation:</strong> Visit the CanoKey documentation for detailed guides
          </Typography>
        </Alert>
      </Box>

      {/* Wizard Dialog */}
      <PIVKeySetupWizard
        open={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
          loadSlotStatuses(); // Refresh after wizard completes
        }}
        dispatch={dispatch}
      />

      {/* Key Import Dialog */}
      <PIVKeyImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        slot={selectedSlot}
        slotName={selectedSlotName}
        dispatch={dispatch}
        onImportSuccess={handleImportSuccess}
      />

      {/* Certificate Generation Dialog */}
      <CertificateGenerationDialog
        open={certDialogOpen}
        onClose={() => setCertDialogOpen(false)}
        slot={selectedSlot}
        slotName={selectedSlotName}
        keyData={importedKeyData}
        dispatch={dispatch}
        onCertificateGenerated={handleCertificateGenerated}
      />
    </Container>
  );
};

export default connect()(Piv);
